<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.faqSectionList.modal.new.buttonText')"
      @click="faqSectionModalOpen = true"
    />
    <export
      url="/faq_sections/csv"
      :filter="filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.faqSections')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <faq-section-modal
    v-model:open="faqSectionModalOpen"
    v-model:section="editedFaqSection"
    @saved="refetch"
  />
  <data-table
    v-model:sort-by="sortKey"
    v-model:sort-direction="sortDirection"
    class="mb-auto ml-12"
    :busy="loading"
    :items="records"
    :columns="columns"
    :visible-columns="visibleColumns"
    :actions="actions"
  />
  <pagination
    v-model:page="page"
    v-model:per-page="perPage"
    class="pagination"
    :total="total"
  />
</template>

<script lang="ts" setup>
import {
  ref, reactive, computed, unref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { collOptionsProvider, useProvider } from '@utils/table/hooks/provider';
import Action from '@utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg';

import type { Filter } from '@utils/filter/Filter.vue';
import { useDelete } from '@hooks/api';
import Tabs from '@components/structure/navigation/Tabs.vue';
import FaqSectionModal from '@pages/content/faq/FaqSectionModal.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import { useTableFields } from '@hooks/table-configuration';
import type { FaqSection, HydraCollection } from '@/entities/Entities';
import { translationFor } from '@/utils/api';
import { useQueryRef } from '@/hooks/url';

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef('filter', {});

const faqSectionModalOpen = ref(false);
const editedFaqSection = ref();

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const [deleteFaq, { loading: deleting }] = useDelete();

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter,
  pagination: {
    page,
    perPage,
  },
});

const actions = [
  {
    title: t('pages.faqSectionList.actions.edit'),
    action: (item: FaqSection) => {
      faqSectionModalOpen.value = true;
      editedFaqSection.value = item;
    },
  },
  {
    title: t('pages.faqSectionList.actions.delete'),
    action: async (item: FaqSection) => {
      if (await deleteFaq(`/faq_sections/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        refetch();
      }
    },
  },
];

const translate = translationFor();

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.faqSectionList.fieldGroups.general'),
    class: 'whitespace-nowrap',
  },
  {
    key: 'translations.title',
    title: t('faqSection.title'),
    group: t('pages.faqSectionList.fieldGroups.general'),
    computed: (item: FaqSection) => translate(item, 'title') ?? '',
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.faqSectionList.filterGroups.general'),
      options: {
        label: t('faqSection.title'),
      },
    },
  },
  {
    key: 'slug',
    title: t('faqSection.slug'),
    group: t('pages.faqSectionList.fieldGroups.general'),
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.faqSectionList.filterGroups.general'),
      options: {
        label: t('faqSection.slug'),
      },
    },
  },
  {
    key: 'showInApp',
    title: t('faqSection.showInApp'),
    group: t('pages.faqSectionList.fieldGroups.general'),
    visible: true,
    formatter: (value: boolean) => (value ? t('yes') : t('no')),
    filter: {
      type: 'bool',
      group: t('pages.faqSectionList.filterGroups.general'),
      options: {
        label: t('faqSection.showInApp'),
      },
    },
  },
  {
    key: 'showInForBusinessApp',
    title: t('faqSection.showInForBusinessApp'),
    group: t('pages.faqSectionList.fieldGroups.general'),
    visible: true,
    formatter: (value: boolean) => (value ? t('yes') : t('no')),
    filter: {
      type: 'bool',
      group: t('pages.faqSectionList.filterGroups.general'),
      options: {
        label: t('faqSection.showInForBusinessApp'),
      },
    },
  },
];

const visibleColumns = useTableFields(
  columns,
);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<FaqSection>>('/faq_sections', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  }));

const loading = computed(() => loadingTable.value || deleting.value);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
.medium {
  width: 300px;
  height: auto;
}
</style>

